// frontend/src/components/chat/ChatWindow.jsx
// PHASE 3 UPDATE: Added typing indicator support
// PHASE 4 UPDATE: Added file attachment support
// PHASE 5 FIX: Fixed auto-scroll when opening conversation
// PHASE 6: Added group chat support
// Chat window displaying message thread with real-time typing

import React, { useRef, useEffect, useState } from "react";
import { useChat } from "../../contexts/ChatContext";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import TypingIndicator from "./TypingIndicator";
import GroupInfoPanel from "./GroupInfoPanel";

const ChatWindow = () => {
  const {
    activeConversation,
    messages,
    isLoadingMessages,
    hasMoreMessages,
    loadMoreMessages,
    sendMessage,
    typingUsers,
    startTyping,
    stopTyping,
    groupParticipants,
    currentUserId,
  } = useChat();

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const hasInitialScrollRef = useRef(false);
  const currentConversationIdRef = useRef(null);

  // State for group info panel
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  // Determine if this is a group conversation
  const isGroup = activeConversation?.is_group === true;

  // Reset scroll tracking when conversation changes
  useEffect(() => {
    if (
      activeConversation?.conversation_id !== currentConversationIdRef.current
    ) {
      hasInitialScrollRef.current = false;
      prevMessagesLengthRef.current = 0;
      currentConversationIdRef.current = activeConversation?.conversation_id;
      setShowGroupInfo(false); // Close group info when switching conversations
    }
  }, [activeConversation?.conversation_id]);

  // Initial scroll to bottom when messages first load for a conversation
  useEffect(() => {
    if (
      messages.length > 0 &&
      !isLoadingMessages &&
      !hasInitialScrollRef.current
    ) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView();
      }, 50);
      hasInitialScrollRef.current = true;
      prevMessagesLengthRef.current = messages.length;
    }
  }, [messages.length, isLoadingMessages]);

  // Scroll to bottom when NEW messages arrive (after initial load)
  useEffect(() => {
    if (
      hasInitialScrollRef.current &&
      messages.length > prevMessagesLengthRef.current
    ) {
      const container = messagesContainerRef.current;
      if (container) {
        const isNearBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
          150;

        if (isNearBottom) {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Handle scroll to load more messages
  const handleScroll = (e) => {
    const container = e.target;
    if (container.scrollTop === 0 && hasMoreMessages && !isLoadingMessages) {
      const scrollHeight = container.scrollHeight;
      loadMoreMessages().then(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - scrollHeight;
      });
    }
  };

  // Handle sending a message (works for both DM and group)
  const handleSendMessage = async (messageText, attachmentData = null) => {
    if (isGroup) {
      // For groups, pass conversation_id instead of recipient_id
      try {
        await sendMessage(null, messageText, {
          conversationId: activeConversation.conversation_id,
          authorizationId: activeConversation.authorization_id,
          consumerId: activeConversation.consumer_id,
          attachment: attachmentData,
        });
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } catch (err) {
        console.error("Failed to send group message:", err);
      }
    } else {
      // Direct message
      if (!activeConversation?.other_participant?.user_id) return;

      try {
        await sendMessage(
          activeConversation.other_participant.user_id,
          messageText,
          {
            authorizationId: activeConversation.authorization_id,
            consumerId: activeConversation.consumer_id,
            attachment: attachmentData,
          }
        );
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } catch (err) {
        console.error("Failed to send message:", err);
      }
    }
  };

  // Handle typing events from input
  const handleTyping = (isTyping) => {
    if (isTyping) {
      startTyping();
    } else {
      stopTyping();
    }
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups = [];
    let currentDate = null;

    messages.forEach((msg) => {
      const msgDate = new Date(msg.created_at).toLocaleDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: "date", date: msgDate, key: `date-${msgDate}` });
      }
      groups.push({ type: "message", message: msg, key: msg.message_id });
    });

    return groups;
  };

  // Format date divider
  const formatDateDivider = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  // Get conversation title
  const getConversationTitle = () => {
    if (isGroup) {
      return activeConversation.group_name || "Group Chat";
    }
    return activeConversation?.other_participant?.name || "Chat";
  };

  // Get participant count text for groups
  const getParticipantCountText = () => {
    if (!isGroup) return null;
    const count = groupParticipants.length;
    return `${count} participant${count !== 1 ? "s" : ""}`;
  };

  const messageGroups = groupMessagesByDate();

  return (
    <div className="chat-window">
      {/* Group Header (shown for groups) */}
      {isGroup && (
        <div className="chat-group-header">
          <div className="chat-group-info">
            <div className="chat-group-avatar">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="chat-group-details">
              <div className="chat-group-name">{getConversationTitle()}</div>
              <div className="chat-group-meta">{getParticipantCountText()}</div>
            </div>
          </div>
          <button
            className="btn btn-link chat-group-info-btn"
            onClick={() => setShowGroupInfo(!showGroupInfo)}
            title="Group info"
          >
            <i
              className={`bi ${showGroupInfo ? "bi-x-lg" : "bi-info-circle"}`}
            ></i>
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={`chat-main-content ${
          showGroupInfo ? "with-info-panel" : ""
        }`}
      >
        {/* Messages Container */}
        <div
          className="chat-messages"
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {/* Load More Indicator */}
          {hasMoreMessages && (
            <div className="chat-load-more">
              {isLoadingMessages ? (
                <div
                  className="spinner-border spinner-border-sm text-secondary"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <button
                  className="btn btn-link btn-sm text-muted"
                  onClick={loadMoreMessages}
                >
                  Load older messages
                </button>
              )}
            </div>
          )}

          {/* Loading State */}
          {isLoadingMessages && messages.length === 0 && (
            <div className="chat-loading">
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span className="visually-hidden">Loading messages...</span>
              </div>
              <span className="ms-2">Loading messages...</span>
            </div>
          )}

          {/* Empty State */}
          {!isLoadingMessages && messages.length === 0 && (
            <div className="chat-empty-messages">
              <i
                className={`bi ${
                  isGroup ? "bi-people" : "bi-chat-square-text"
                } d-block fs-1 mb-2 text-muted`}
              ></i>
              <p className="text-muted">No messages yet</p>
              <p className="text-muted small">
                {isGroup
                  ? "Start the group conversation by sending a message below"
                  : "Start the conversation by sending a message below"}
              </p>
            </div>
          )}

          {/* Messages */}
          {messageGroups.map((group) => {
            if (group.type === "date") {
              return (
                <div key={group.key} className="chat-date-divider">
                  <span>{formatDateDivider(group.date)}</span>
                </div>
              );
            }
            return (
              <ChatMessage
                key={group.key}
                message={group.message}
                isGroup={isGroup}
              />
            );
          })}

          {/* Typing Indicator */}
          {typingUsers && typingUsers.length > 0 && (
            <TypingIndicator typingUsers={typingUsers} isGroup={isGroup} />
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Group Info Panel (side panel for groups) */}
        {showGroupInfo && isGroup && (
          <GroupInfoPanel
            conversation={activeConversation}
            onClose={() => setShowGroupInfo(false)}
          />
        )}
      </div>

      {/* Input */}
      <ChatInput onSend={handleSendMessage} onTyping={handleTyping} />
    </div>
  );
};

export default ChatWindow;
